import { useMsal } from "@azure/msal-react";
import {
    Card,
    Checkbox,
    Flex,
    Grid,
    Group,
    Paper,
    Select,
    Stack,
    Text,
    TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import { PdfFocusProvider } from "src/components/PDFViewer/context";
import { ChatRole, db, MessageEntity } from "src/db";
import { type ChatMessage, useChatCompletion } from "src/hooks/useChatCompletion";
import { postAPI } from "src/utils/fetch";
import { v4 as uuidv4 } from "uuid";

const chatId = uuidv4();

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
export const SpotLightRoute = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [filters, setFilters] = useState({});
    const [sortBy, setSortBy] = useState("relevance");
    const { instance } = useMsal();
    const chatCompletion = useChatCompletion();

    const _suggestAnswer = async (debouncedSearchTerm: string, userMessageId: string) => {
        // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        const messagesSending = await (chatCompletion as any).makeMessagesSendingRequest({
            chatId,
            content: debouncedSearchTerm,
        });

        const assistantMessageReceivedId = await MessageEntity._()
            .setChatId(chatId)
            .setRole(ChatRole.ASSISTANT)
            .setContent("")
            .setRepliedId(userMessageId)
            .add();

        chatCompletion.send(
            messagesSending,
            async (chatMessage: ChatMessage | string, error?: string) => {
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                await updateAssistantMessage(assistantMessageReceivedId as any, chatMessage, error);
            },
        );
    };

    const updateAssistantMessage = async (
        messageId: string,
        chatMessage: ChatMessage | string,
        error?: string,
    ) => {
        await db.messages.where({ id: messageId }).modify((message) => {
            message.done = true;
            if (typeof chatMessage === "string") {
                message.content = chatMessage;
            } else {
                message.content = chatMessage.content;
                message.responseId = chatMessage.responseId;
                message.pretrained = chatMessage.pretrained;
                message.suggestions = chatMessage.suggestions;
            }
            message.hasError = Boolean(error);
            message.error = error;
        });
    };

    const fetchSearchResults = async () => {
        const url = new URL("v1/search/text", BACKEND_URL);

        // Only suggest answer if it hasn't been done for this search term
        if (!messages?.some((m) => m.content === debouncedSearchTerm && m.role === ChatRole.USER)) {
            const _userMessageId = await MessageEntity._()
                .setChatId(chatId)
                .setRole(ChatRole.USER)
                .setContent(debouncedSearchTerm)
                .add();

            // suggestAnswer(debouncedSearchTerm, userMessageId);
        }

        const response = await postAPI({
            url: url.toString(),
            instance,
            payload: {
                query: debouncedSearchTerm,
                metadata_filter: Object.entries(filters).flatMap(([key, value]) => {
                    if (Array.isArray(value)) {
                        return value.map((v) => ({ key, value: v }));
                    }
                    return [{ key, value: value.toString() }];
                }),
                sort: sortBy,
            },
        });
        return response.json();
    };

    const messages = useLiveQuery(() => {
        if (!chatId) return [];
        return db.messages.where("chatId").equals(chatId).sortBy("createdAt");
    }, [chatId]);

    const { data, error, refetch } = useQuery({
        queryKey: ["searchResults", debouncedSearchTerm, filters, sortBy],
        queryFn: fetchSearchResults,
        enabled: !!debouncedSearchTerm,
    });

    const handleFilterChange = (key, value, checked) => {
        setFilters((prev) => ({
            ...prev,
            [key]: checked
                ? [...(prev[key] || []), value]
                : (prev[key] || []).filter((v) => v !== value),
        }));
    };

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 1000); // 1000ms debounce

        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    useEffect(() => {
        if (debouncedSearchTerm) {
            refetch();
        }
    }, [debouncedSearchTerm, refetch]);

    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
    if (error) return <Text>An error occurred: {(error as any).message}</Text>;

    const { aggregations, hits } = data || {
        aggregations: {},
        hits: { total: { value: 0 }, hits: [] },
    };

    return (
        <PdfFocusProvider>
            <Paper
                p="md"
                radius="sm"
                withBorder
                style={{
                    height: "calc(100vh - 60px)",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Flex justify="center" mb="xl">
                    <TextInput
                        placeholder="When was the last gas turbine changeout at North Rankin?"
                        leftSection={<IconSearch size={14} />}
                        size="md"
                        style={{ width: "70%" }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Flex>

                <Grid>
                    <Grid.Col span={9}>
                        <Text fw={700} size="lg" mb="md">
                            {searchTerm}
                        </Text>

                        <Group justify="space-between" mb="md">
                            <Group gap="xs">
                                <Text fw={700}>Results {hits.total.value}</Text>
                            </Group>
                            <Group gap="xs">
                                <Select
                                    placeholder="Sort: relevance"
                                    data={[
                                        { value: "relevance", label: "Relevance" },
                                        { value: "date", label: "Date" },
                                    ]}
                                    styles={{ root: { width: 150 } }}
                                    value={sortBy}
                                    onChange={setSortBy}
                                />
                                <Text size="sm" color="blue">
                                    Hide filters
                                </Text>
                            </Group>
                        </Group>

                        {hits.hits.map((hit) => (
                            <Card key={hit._id} withBorder mb="md">
                                <Text fw={700}>
                                    {hit._source.metadata["Subject/Title"] ||
                                        `Result for ${hit._id}`}
                                </Text>
                                <Text size="sm" color="dimmed" mb="xs">
                                    {hit._id}
                                </Text>
                                {hit.highlight?.text?.map((highlight) => (
                                    <Text
                                        key={`highlight-${highlight}`}
                                        size="sm"
                                        mb="xs"
                                        component="div"
                                    >
                                        <span>{highlight}</span>
                                    </Text>
                                ))}
                            </Card>
                        ))}
                    </Grid.Col>

                    <Grid.Col span={3}>
                        <Text fw={700} mb="md">
                            Aggregations
                        </Text>
                        <Stack gap="xs">
                            {Object.entries(aggregations)
                                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                                .sort(([, a]: [string, any], [, b]: [string, any]) => {
                                    const aTotal =
                                        a.buckets?.reduce(
                                            (sum, bucket) => sum + bucket.doc_count,
                                            0,
                                        ) || 0;
                                    const bTotal =
                                        b.buckets?.reduce(
                                            (sum, bucket) => sum + bucket.doc_count,
                                            0,
                                        ) || 0;
                                    return bTotal - aTotal;
                                })
                                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                                .map(([key, value]: [string, any]) => {
                                    if (value.buckets && value.buckets.length > 0) {
                                        const sortedBuckets = value.buckets.sort(
                                            (a, b) => b.doc_count - a.doc_count,
                                        );
                                        return (
                                            <Card key={key} withBorder mb="sm">
                                                <Text fw={600} size="sm">
                                                    {key}
                                                </Text>
                                                {sortedBuckets.map(
                                                    (bucket, _index) =>
                                                        bucket.doc_count > 0 && (
                                                            <Checkbox
                                                                key={`${key}-${bucket.key}`}
                                                                label={`${bucket.key} (${bucket.doc_count})`}
                                                                mb="xs"
                                                                onChange={(event) =>
                                                                    handleFilterChange(
                                                                        key,
                                                                        bucket.key,
                                                                        event.currentTarget.checked,
                                                                    )
                                                                }
                                                                checked={
                                                                    filters[key]?.includes(
                                                                        bucket.key,
                                                                    ) || false
                                                                }
                                                            />
                                                        ),
                                                )}
                                            </Card>
                                        );
                                    }
                                    return null;
                                })}
                        </Stack>
                    </Grid.Col>
                </Grid>
            </Paper>
        </PdfFocusProvider>
    );
};
